import apiClient from './api/client';
import { API_ENDPOINTS } from './api/endpoints';
import { Content, Post, Article, Video, ContentFilters, Region } from '../types/content';
import { PaginatedResponse, QueryParams } from '../types/api';

class ContentService {
  // Feed methods
  async getFeed(params: ContentFilters & QueryParams): Promise<PaginatedResponse<Content>> {
    return apiClient.get('/feed', { params });
  }

  async getRegionalContent(region: Region, params?: QueryParams): Promise<PaginatedResponse<Content>> {
    return apiClient.get('/regional-content', { params: { ...params, region } });
  }

  async getExploreContent(params?: QueryParams): Promise<PaginatedResponse<Content>> {
    return apiClient.get('/explore', { params });
  }

  async getTrendingContent(region?: Partial<Region>): Promise<PaginatedResponse<Content>> {
    return apiClient.get('/trending', { params: { region } });
  }

  // Content CRUD
  async createPost(data: Partial<Post>): Promise<Post> {
    return apiClient.post(API_ENDPOINTS.content.posts, data);
  }

  async createArticle(data: Partial<Article>): Promise<Article> {
    return apiClient.post(API_ENDPOINTS.content.articles, data);
  }

  async createVideo(data: Partial<Video>): Promise<Video> {
    return apiClient.post(API_ENDPOINTS.content.videos, data);
  }

  async updateContent(type: string, id: string, data: Partial<Content>): Promise<Content> {
    return apiClient.put(`${API_ENDPOINTS.content[type as keyof typeof API_ENDPOINTS.content]}/${id}`, data);
  }

  async deleteContent(type: string, id: string): Promise<void> {
    return apiClient.delete(`${API_ENDPOINTS.content[type as keyof typeof API_ENDPOINTS.content]}/${id}`);
  }

  // Content interactions
  async likeContent(type: string, id: string): Promise<void> {
    return apiClient.post(API_ENDPOINTS.interactions.like(type, id));
  }

  async repostContent(type: string, id: string): Promise<void> {
    return apiClient.post(API_ENDPOINTS.interactions.repost(type, id));
  }

  async bookmarkContent(type: string, id: string): Promise<void> {
    return apiClient.post(API_ENDPOINTS.interactions.bookmark(type, id));
  }

  // Content discovery
  async searchContent(query: string, filters?: ContentFilters): Promise<PaginatedResponse<Content>> {
    return apiClient.get('/search', { params: { query, ...filters } });
  }

  async getRelatedContent(contentId: string, type: string): Promise<Content[]> {
    return apiClient.get(`/content/${contentId}/related`, { params: { type } });
  }

  // Analytics
  async getContentStats(contentId: string): Promise<{
    views: number;
    likes: number;
    shares: number;
    comments: number;
    engagement: number;
  }> {
    return apiClient.get(`/content/${contentId}/stats`);
  }

  async getRegionalStats(region: Region): Promise<{
    totalContent: number;
    activeUsers: number;
    topCategories: { name: string; count: number }[];
    engagement: { likes: number; comments: number; shares: number };
  }> {
    return apiClient.get('/stats/regional', { params: { region } });
  }

  // Content translation
  async translateContent(contentId: string, targetLanguage: string): Promise<{
    translatedContent: string;
    language: string;
  }> {
    return apiClient.post(`/content/${contentId}/translate`, { language: targetLanguage });
  }
}

export default new ContentService();